import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles, Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import { isLastIndex } from 'utils/array-helpers';
import { commonColumns, getCellFormat } from '../utils';
import { TableHeader } from '../../common/components';

import TypographyWithTooltip from '../../../../shared/TypographyWithTooltip';

const useStyles = makeStyles(theme => ({
  row: {
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: 'unset',
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:first-child': {
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
        maxWidth: theme.customStyles.cuttingTextCompactMode.maxWidth,
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  title: {
    fontSize: theme.customStyles.tableRow.fontSize
  },
  year: {
    fontWeight: theme.typography.fontWeightBold
  },
  negative: {
    color: theme.palette.error.main
  }
}));

const YearDataTable = ({ timeSeries, data, maxYear, timeseriesToolTipnames }) => {
  const columns = [
    { id: 1, key: 'timeSeries', title: data.year, isSortable: false },
    ...commonColumns
  ];
  const classes = useStyles();

  const rows = timeSeries.map((ts, tsIndex) => {
    const row = { timeSeries: ts.label, isActive: ts.isActive, toolTip: timeseriesToolTipnames[tsIndex] };

    data.monthlyEntries.forEach((monthlyEntry, montlyEntryIndex) => {
      const month = moment(monthlyEntry.reportingDate).format('MMM').toLowerCase();
      const rowKey = isLastIndex(data.monthlyEntries, montlyEntryIndex) ? 'year' : month;
      row[rowKey] = monthlyEntry.values[tsIndex]?.toString();
      if (tsIndex === 0 && maxYear === moment(monthlyEntry.reportingDate).format('YYYY')) {
        monthlyEntry.estimate && (row[rowKey+'estimated'] = monthlyEntry.estimate);
      }
    });
    return row;
  });

  const renderRow = row => {
    return columns.map(column => {
      const { value, classList } = getCellFormat(column.key, row[column.key]);
      const timeSeriesLabels = timeSeries.map(timeserieObj => timeserieObj.label);
      const showTimeserieToolTipFlag = timeSeriesLabels.includes(value) && timeseriesToolTipnames.length > 0;
      return (
        <TableCell key={`${column.key}-${row.id}`} style={{ backgroundColor: (row[column.key+'estimated'] && value) && 'yellow' }}>
          <TypographyWithTooltip
            variant="subtitle1"
            showTimeserieToolTip={showTimeserieToolTipFlag}
            className={classes.title}
            tooltipProps={{
              value: showTimeserieToolTipFlag ? row.toolTip : value,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <span className={classList.map(c => classes[c]).join(' ')}>{value}</span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });
  };

  const renderBody = () =>
    rows.map(
      row =>
        row.isActive && (
          <TableRow key={`${data.year}-${row.timeSeries}`} className={classes.row}>
            {renderRow(row)}
          </TableRow>
        )
    );

  return (
    <Table>
      <TableHeader
        columns={columns}
        showHeaderBorder={false}
        firstColumnFixedWidth={50}
        columnFixedWidth={0}
      />
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
};

YearDataTable.propTypes = {
  timeSeries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isActive: PropTypes.bool.isRequired
    }).isRequired
  ).isRequired,
  data: PropTypes.shape({
    year: PropTypes.string.isRequired,
    monthlyEntries: PropTypes.arrayOf(
      PropTypes.shape({
        reportingDate: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.number).isRequired
      }).isRequired
    ).isRequired
  }).isRequired
};

export default YearDataTable;
